import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { useGlobal } from "reactn";
import PropTypes from "prop-types";

import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Input from "shared-anpost-components/components/form/input/Input";
import FieldError from "shared-anpost-components/components/form/fieldError/FieldError";
import Select from "shared-anpost-components/components/form/select/Select";
import Label from "shared-anpost-components/components/form/label/Label";

import { VerifyPhoneNumberStyled } from "./VerifyPhoneNumberStyled";
import Icon from "../../../_common/icons/ReturnIcons";
import Button from "../../../_common/utils/Button";
import ErrorMessage from "../../errorMessage";
import { virtualPageView } from "../../../../helpers/Helpers";

/**
 * @param {{
 *  submitHandler: (country: string, number: string) => void,
 *  hasError: boolean,
 *  resetFields: () => void
 *  singleItem: boolean,
 * }} props
 */

const VerifyPhoneNumberStageOne = forwardRef(({ submitHandler, hasError, resetFields, singleItem }, ref) => {
    const [userProfile] = useGlobal("userProfile");

    const [phoneNr, setPhoneNr] = useState("");
    const [country, setCountry] = useState("");
    const [numberError, setNumberError] = useState("");
    const [focused, setFocused] = useState(["Country"]);

    useImperativeHandle(ref, () => ({
        triggerAction() {
            setPhoneNr("");
            setCountry("Ireland +353");
            setNumberError("");
            setFocused(["Country"]);
        }
    }));

    const hasPhoneInProfile = userProfile?.phones?.length > 0;

    const numbersConfig = [
        { id: "+44", value: "UK +44" },
        { id: "+353", value: "Ireland +353" }
    ];

    useEffect(() => {
        if (hasPhoneInProfile) {
            const phone = userProfile.phones[0];
            if (phone.number.startsWith("+44")) {
                const countryCode = "UK +44";
                const slicedPhoneNumber = phone.number.slice(3);
                setCountry(countryCode);
                setPhoneNr(slicedPhoneNumber);
            } else if (phone.number.startsWith("+353")) {
                const countryCode = "Ireland +353";
                const slicedPhoneNumber = phone.number.slice(4);
                setCountry(countryCode);
                setPhoneNr(slicedPhoneNumber);
            }
        } else {
            setCountry("Ireland +353");
        }
    }, [userProfile]);

    useEffect(() => {
        let pageViewUrl = window.location.pathname?.replace(/\/+$/, ""); // remove trailing slash
        if (singleItem) {
            pageViewUrl = `${pageViewUrl}/item`;
        }
        virtualPageView(`${pageViewUrl}/Add-your-mobile-number`, `${document.title} - Add your mobile phone number`);
    }, []);

    const handlePhoneNumberBlur = () => {
        const isNumeric = /^[0-9]*$/.test(phoneNr);
        if (phoneNr === "") {
            const newVal = focused.filter(item => item !== "number");
            setFocused(newVal);
            setNumberError("Mobile number is required");
        } else if (!isNumeric) {
            setNumberError("Phone number should contain only digits.");
        } else if (phoneNr !== "") {
            if (!focused.includes("number")) {
                setFocused([...focused, "number"]);
            }
            if (country === "Ireland +353") {
                if (phoneNr.length < 9 || phoneNr.length > 10) {
                    setNumberError("Mobile number is an incorrect length, please check and try again");
                } else if (phoneNr.length === 9 && phoneNr[0] !== "8") {
                    setNumberError("Mobile number is an incorrect length, please check and try again");
                } else if (phoneNr.length === 10 && (phoneNr[0] !== "0" || phoneNr[1] !== "8")) {
                    setNumberError("Mobile number must start with 08");
                } else {
                    setNumberError("");
                }
            }
            if (country === "UK +44") {
                if (phoneNr.length < 10 || phoneNr.length > 11) {
                    setNumberError("Mobile number is an incorrect length, please check and try again");
                } else if (phoneNr.length === 10 && phoneNr[0] !== "7") {
                    setNumberError("Mobile number is an incorrect length, please check and try again");
                } else if ((phoneNr.length === 11 && phoneNr[0] !== "0") || phoneNr[1] !== "7") {
                    setNumberError("Mobile number must start with 07");
                } else {
                    setNumberError("");
                }
            }
        } else {
            setNumberError("");
        }
    };

    const handleInputChange = event => {
        const newValue = event.target.value.replace(/\s+/g, "");
        if (/^\d*$/.test(newValue)) {
            setPhoneNr(newValue);
        }
    };

    const handleFocus = event => {
        const { name, value } = event.target;
        const newVal = value && phoneNr ? focused.filter(item => item !== name) : [...focused, name];
        setFocused(newVal);
    };

    const verifyAndSubmit = event => {
        event.preventDefault();
        let filteredCountryNr;
        if (country.includes("Ireland") || country.includes("UK")) {
            filteredCountryNr = country.split(" ")[1];
        } else {
            filteredCountryNr = country;
        }
        handlePhoneNumberBlur();
        if (phoneNr !== "" && numberError === "") {
            submitHandler(filteredCountryNr, phoneNr);
        }
    };

    const handleRetry = () => {
        resetFields();
    };

    return (
        <VerifyPhoneNumberStyled>
            <>
                <div className="titleWrapper">
                    <Icon src="mobileVerificationIcon" />
                    <div className="headerTitle">Add a mobile phone number</div>
                    {!hasError ? <span className="titleInfo">Please enter your mobile number. We’ll send you a code by SMS to verify it. </span> : ""}
                </div>
                {hasError ? (
                    <ErrorMessage message="Sorry, we couldn’t validate your phone number" tryAgainEvent={handleRetry} isPhone />
                ) : (
                    <>
                        <div>
                            <form onSubmit={verifyAndSubmit}>
                                <div className="inputWrapper">
                                    <FormGroup>
                                        <Select
                                            id="Country"
                                            showLabel
                                            type="text"
                                            name="Country"
                                            focused={focused.includes("Country")}
                                            labelText="Country"
                                            options={numbersConfig}
                                            selectedValue={country}
                                            defaultValue={hasPhoneInProfile ? country : "Ireland +353"}
                                            required
                                            disabled={hasPhoneInProfile}
                                            change={event => setCountry(event.target.value)}
                                            maxLength="50"
                                        />
                                    </FormGroup>

                                    <FormGroup error={numberError !== ""} focused={focused.includes("number") || phoneNr}>
                                        <InputGroup>
                                            <Label htmlFor="number" text="Mobile" />
                                            <Input
                                                id="number"
                                                type="tel"
                                                name="number"
                                                required
                                                focus={handleFocus}
                                                blur={handlePhoneNumberBlur}
                                                value={phoneNr}
                                                disabled={hasPhoneInProfile}
                                                change={handleInputChange}
                                                maxLength="11"
                                            />
                                        </InputGroup>
                                        <div className="errorWrapper">{numberError !== "" && <FieldError error={numberError} />}</div>
                                    </FormGroup>
                                </div>

                                <div className="submitPanel">
                                    <Button type="submit" className="gtm-cta">
                                        Send
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </>
        </VerifyPhoneNumberStyled>
    );
});

export default VerifyPhoneNumberStageOne;

VerifyPhoneNumberStageOne.displayName = "VerifyPhoneNumberStageOne";
VerifyPhoneNumberStageOne.propTypes = {
    submitHandler: PropTypes.func.isRequired,
    hasError: PropTypes.bool.isRequired,
    resetFields: PropTypes.func.isRequired,
    singleItem: PropTypes.bool
};
